import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { DeepAnalysis } from './useDeepAnalysis';

interface PublishResult {
  success: boolean;
  articleId?: string;
  slug?: string;
  error?: string;
}

export const usePublishAnalysis = () => {
  const [isPublishing, setIsPublishing] = useState(false);
  const { toast } = useToast();

  const generateSlug = (name: string, symbol: string): string => {
    const date = new Date().toISOString().split('T')[0];
    const baseSlug = `${name}-${symbol}-analysis-${date}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    return baseSlug;
  };

  const generateArticleContent = (analysis: DeepAnalysis): string => {
    const { tokenInfo, marketData, technicalAnalysis, fundamentalAnalysis, sentimentAnalysis, riskAssessment, catalysts, tradingSetup, keyInsights, detailedAnalysis, executiveSummary, verdict, confidence, priceTarget } = analysis;
    
    const formatPrice = (price: number | null | undefined) => {
      if (price === null || price === undefined || !Number.isFinite(price)) return 'Unavailable';
      if (price < 0.01) return `$${price.toFixed(6)}`;
      if (price < 1) return `$${price.toFixed(4)}`;
      return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatMarketCap = (mc: number | null | undefined) => {
      if (mc === null || mc === undefined || !Number.isFinite(mc)) return 'Unavailable';
      if (mc >= 1e9) return `$${(mc / 1e9).toFixed(2)}B`;
      if (mc >= 1e6) return `$${(mc / 1e6).toFixed(2)}M`;
      return `$${mc.toLocaleString()}`;
    };
    const athRow = analysis.historicalIndicators?.ath_price_usd
      ? `| ATH Recovery Signal | Currently trading ${analysis.historicalIndicators.ath_drawdown_pct?.toFixed(2) ?? 'N/A'}% below its all-time high of ${formatPrice(analysis.historicalIndicators.ath_price_usd)} (reached ${analysis.historicalIndicators.ath_date || 'date unavailable'}) |`
      : '| ATH Recovery Signal | 5/10 (neutral) — ATH data unavailable from market source |';

    const verdictEmoji = {
      'STRONG_BUY': '🚀',
      'BUY': '✅',
      'HOLD': '⏸️',
      'SELL': '⚠️',
      'STRONG_SELL': '🛑'
    };

    return `
## Executive Summary

${executiveSummary}

**AI Verdict: ${verdictEmoji[verdict] || ''} ${verdict.replace('_', ' ')}** (${confidence}% confidence)

---

## Market Overview

| Metric | Value |
|--------|-------|
| Current Price | ${formatPrice(marketData.price)} |
| Market Cap | ${formatMarketCap(marketData.marketCap)} |
| 24h Volume | ${formatMarketCap(marketData.volume)} |
| 24h Change | ${marketData.priceChange24h >= 0 ? '+' : ''}${marketData.priceChange24h.toFixed(2)}% |
| 7d Change | ${marketData.priceChange7d >= 0 ? '+' : ''}${marketData.priceChange7d.toFixed(2)}% |
| 30d Change | ${marketData.priceChange30d >= 0 ? '+' : ''}${marketData.priceChange30d.toFixed(2)}% |
${athRow}

---

## Price Targets

| Scenario | Target | Potential |
|----------|--------|-----------|
| 🐻 Bearish | ${formatPrice(priceTarget.bearish)} | ${(((priceTarget.bearish - marketData.price) / marketData.price) * 100).toFixed(1)}% |
| 📊 Base | ${formatPrice(priceTarget.base)} | ${(((priceTarget.base - marketData.price) / marketData.price) * 100).toFixed(1)}% |
| 🐂 Bullish | ${formatPrice(priceTarget.bullish)} | ${(((priceTarget.bullish - marketData.price) / marketData.price) * 100).toFixed(1)}% |

---

## Technical Analysis

**Score: ${technicalAnalysis.score}/100** | **Trend: ${technicalAnalysis.trend}**

${technicalAnalysis.analysis}

### Key Levels
- **Support:** ${formatPrice(technicalAnalysis.keyLevels.support)}
- **Resistance:** ${formatPrice(technicalAnalysis.keyLevels.resistance)}

### Technical Signals
${technicalAnalysis.signals.map(s => `- ${s}`).join('\n')}

---

## Fundamental Analysis

**Score: ${fundamentalAnalysis.score}/100** | **Grade: ${fundamentalAnalysis.grade}**

${fundamentalAnalysis.analysis}

### Strengths
${fundamentalAnalysis.strengths.map(s => `- ✅ ${s}`).join('\n')}

### Weaknesses
${fundamentalAnalysis.weaknesses.map(w => `- ⚠️ ${w}`).join('\n')}

---

## Sentiment Analysis

**Score: ${sentimentAnalysis.score}/100** | **Overall: ${sentimentAnalysis.overall}**

${sentimentAnalysis.analysis}

### Key Sentiment Drivers
${sentimentAnalysis.keyDrivers.map(d => `- ${d}`).join('\n')}

---

## Risk Assessment

**Risk Level: ${riskAssessment.level}** | **Risk Score: ${riskAssessment.score}/100**

### Risk Factors
${riskAssessment.factors.map(f => `- 🔴 ${f}`).join('\n')}

### Mitigations
${riskAssessment.mitigations.map(m => `- 🟢 ${m}`).join('\n')}

---

## Trading Setup

| Parameter | Value |
|-----------|-------|
| Entry Zone | ${tradingSetup.entry.zone} |
| Entry Price | ${formatPrice(tradingSetup.entry.price)} |
| Stop Loss | ${formatPrice(tradingSetup.stopLoss.price)} (-${tradingSetup.stopLoss.percentage}%) |
| Position Size | ${tradingSetup.positionSize} |

### Price Targets
${tradingSetup.targets.map((t, i) => `- **Target ${i + 1}:** ${formatPrice(t.price)} (+${t.percentage}%)`).join('\n')}

---

## Upcoming Catalysts

${catalysts.upcoming.map(c => `- 📅 ${c}`).join('\n')}

### Potential Risks
${catalysts.risks.map(r => `- ⚠️ ${r}`).join('\n')}

---

## Key Insights

${keyInsights.map(i => `- 💡 ${i}`).join('\n')}

---

## Detailed Analysis

${detailedAnalysis}

---

*This analysis was generated by AI on ${new Date(analysis.analyzedAt).toLocaleDateString()} and is for informational purposes only. Always do your own research before making investment decisions.*
`.trim();
  };

  const publishAnalysis = async (analysis: DeepAnalysis): Promise<PublishResult> => {
    setIsPublishing(true);
    
    try {
      const { tokenInfo, marketData, verdict, confidence, overallScore, executiveSummary, keyInsights } = analysis;
      
      const slug = generateSlug(tokenInfo.name, tokenInfo.symbol);
      const content = generateArticleContent(analysis);
      
      // Create headline
      const headline = `${tokenInfo.name} (${tokenInfo.symbol.toUpperCase()}) Analysis: ${verdict.replace('_', ' ')} Signal with ${confidence}% Confidence`;
      
      // Create summary from executive summary
      const summary = executiveSummary.length > 300 
        ? executiveSummary.substring(0, 297) + '...' 
        : executiveSummary;
      
      // SEO fields
      const seoTitle = `${tokenInfo.name} Price Analysis & Prediction | ${verdict.replace('_', ' ')} | Crypto Tips`;
      const seoDescription = `AI-powered analysis of ${tokenInfo.name} (${tokenInfo.symbol.toUpperCase()}). Current price: $${marketData.price.toFixed(4)}. Verdict: ${verdict.replace('_', ' ')} with ${confidence}% confidence. Get trading insights and price targets.`;
      const seoKeywords = [
        tokenInfo.symbol.toLowerCase(),
        tokenInfo.name.toLowerCase(),
        `${tokenInfo.symbol.toLowerCase()} price`,
        `${tokenInfo.symbol.toLowerCase()} analysis`,
        `${tokenInfo.symbol.toLowerCase()} prediction`,
        'crypto analysis',
        'ai crypto analysis',
        ...tokenInfo.categories.slice(0, 3).map(c => c.toLowerCase())
      ];

      // Check if article with this slug already exists
      const { data: existingArticle } = await supabase
        .from('articles')
        .select('id')
        .eq('slug', slug)
        .maybeSingle();

      let articleId: string;

      if (existingArticle) {
        // Update existing article
        const { data: updatedArticle, error: updateError } = await supabase
          .from('articles')
          .update({
            headline,
            content,
            summary,
            category: 'analysis',
            primary_token: tokenInfo.symbol.toUpperCase(),
            tokens_featured: [tokenInfo.symbol.toUpperCase()],
            opportunity_score: overallScore,
            ai_recommendation: verdict,
            seo_title: seoTitle,
            seo_description: seoDescription,
            seo_keywords: seoKeywords,
            word_count: content.split(/\s+/).length,
            reading_time: Math.ceil(content.split(/\s+/).length / 200),
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingArticle.id)
          .select('id')
          .single();

        if (updateError) throw updateError;
        articleId = updatedArticle.id;
        
        toast({
          title: "Article Updated",
          description: `${tokenInfo.name} analysis has been updated`,
        });
      } else {
        // Insert new article
        const { data: newArticle, error: insertError } = await supabase
          .from('articles')
          .insert({
            slug,
            headline,
            content,
            summary,
            category: 'analysis',
            primary_token: tokenInfo.symbol.toUpperCase(),
            tokens_featured: [tokenInfo.symbol.toUpperCase()],
            opportunity_score: overallScore,
            ai_recommendation: verdict,
            seo_title: seoTitle,
            seo_description: seoDescription,
            seo_keywords: seoKeywords,
            status: 'published',
            word_count: content.split(/\s+/).length,
            reading_time: Math.ceil(content.split(/\s+/).length / 200),
          })
          .select('id')
          .single();

        if (insertError) throw insertError;
        articleId = newArticle.id;
        
        toast({
          title: "Article Published",
          description: `${tokenInfo.name} analysis is now live`,
        });
      }

      return { success: true, articleId, slug };
    } catch (err) {
      console.error('Publish error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to publish article';
      
      toast({
        title: "Publish Failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      return { success: false, error: errorMessage };
    } finally {
      setIsPublishing(false);
    }
  };

  return {
    publishAnalysis,
    isPublishing,
  };
};
